import type { Joi } from '@ksyos/koi';
import type { SortingState } from '@tanstack/react-table';
import { DateTime } from '@ksyos/date-time';
import {
    ActionBar,
    Alert,
    Box,
    Button,
    DatePicker,
    EmptyState,
    FormGroup,
    Loader,
    PageHeader,
    PageLayout,
    Pagination,
    Select,
    SplitButton,
    Text,
} from '@ksyos/design-system';
import { Koi } from '@ksyos/koi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Profession, ToastType } from '@common/enums';
import { formatString } from '@common/utils/formatString';
import { getDisplayName } from '@common/utils/getDisplayName';
import { replaceKoiError } from '@common/validation/replaceKoiError';
import { BadgeWithLabel } from '@frontend/components/elements/BadgeWithLabel';
import { Modal } from '@frontend/components/elements/modals/Modal';
import { OutpatientClinicTaskListAuthorization } from '@frontend/components/pages/outpatientClinic/components/OutpatientClinicTaskListAuthorization';
import { OutpatientClinicTaskListFilterPanel } from '@frontend/components/pages/outpatientClinic/components/OutpatientClinicTaskListFilterPanel';
import { OutpatientClinicTaskListNoResults } from '@frontend/components/pages/outpatientClinic/components/OutpatientClinicTaskListNoResults';
import { OutpatientClinicTaskListTable } from '@frontend/components/pages/outpatientClinic/components/OutpatientClinicTaskListTable';
import { useFilterSearchParams } from '@frontend/components/pages/outpatientClinic/hooks/useFilterSearchParams';
import { useOutpatientClinicTasks } from '@frontend/components/pages/outpatientClinic/hooks/useOutpatientClinicTasks';
import { usePaginationSearchParams } from '@frontend/components/pages/outpatientClinic/hooks/usePaginationSearchParams';
import { useSortingSearchParams } from '@frontend/components/pages/outpatientClinic/hooks/useSortingSearchParams';
import { useTableSelection } from '@frontend/components/pages/outpatientClinic/hooks/useTableSelection';
import { fetchOutpatientClinicTasksAvailablePerformers } from '@frontend/components/pages/outpatientClinic/services/fetchOutpatientClinicTasksAvailablePerformers';
import { useFormData } from '@frontend/contexts/support/application/hooks/useFormData';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation, showToast } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';

type ActiveModal = 'editPerformer' | 'editExpectedDateCompleted';

export function OutpatientClinicTaskList() {
    const translate = getSelectedLanguage();
    const abortSignal = useAbortSignal();
    const queryClient = useQueryClient();

    const [filter, setFilter] = useFilterSearchParams();
    const [pagination, setPagination] = usePaginationSearchParams();
    const [sorting, setSorting] = useSortingSearchParams();

    const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);
    const tableSelection = useTableSelection<string>();
    const [isSaving, setIsSaving] = useState(false);

    const editPerformerFormData = useFormData<{ practitionerIdOrProfession: string | Profession | null }>(
        Koi.object({
            practitionerIdOrProfession: Koi.alternatives().try(Koi.string().uuid(), Koi.koi().enum(Profession)),
        })
    );

    const editExpectedDateCompletedFormData = useFormData<{ editExpectedDateCompleted: Date | null }>(
        Koi.object({
            editExpectedDateCompleted: Koi.date()
                .custom(assertIsWeekday)
                .error((errors: Joi.ErrorReport[]) => replaceKoiError(errors, 'any.custom', 'notValidWeekend')),
        }),
        {
            editExpectedDateCompleted: null,
        }
    );

    function assertIsWeekday(expectedDateCompleted: Date | string) {
        if (DateTime.from(expectedDateCompleted).isWeekend()) {
            throw new Error('Only working days are available');
        }

        return expectedDateCompleted;
    }

    const { tasks, total, isLoading, error, episodeCategories, types, practitioners, professions } =
        useOutpatientClinicTasks({ filter, pagination, sorting });

    const previousFilterRef = useRef(filter);
    useEffect(() => {
        if (!isEqual(previousFilterRef.current, filter)) {
            setPagination((prev) => ({
                pageIndex: 0,
                pageSize: prev.pageSize,
            }));
        }

        previousFilterRef.current = filter;
    }, [filter, setPagination]);

    useEffect(() => {
        editPerformerFormData.reset();
    }, [activeModal]);

    const { data: availablePerformers = [] } = useQuery({
        enabled: activeModal === 'editPerformer',
        queryFn: () =>
            fetchOutpatientClinicTasksAvailablePerformers({ abortSignal, taskId: tableSelection.getSelected() }),
        queryKey: ['outpatient-clinic-tasks-available-performers'],
    });

    return (
        <OutpatientClinicTaskListAuthorization>
            <PageLayout>
                <PageHeader title="Taken management" />
                <OutpatientClinicTaskListFilterPanel
                    episodeCategories={episodeCategories}
                    filter={filter}
                    practitioners={practitioners}
                    professions={professions}
                    setFilter={setFilter}
                    types={types}
                />
                {renderContent()}
                {renderActionBar()}
                {renderEditExpectedDateCompletedModal()}
                {renderEditPerformerModal()}
            </PageLayout>
        </OutpatientClinicTaskListAuthorization>
    );

    function openModal(modal: ActiveModal) {
        setActiveModal(modal);
    }

    function closeModal() {
        setActiveModal(null);
    }

    function getTaskTranslation(amount: number) {
        return amount === 1 ? translate.outpatientClinic.tasks.singular : translate.outpatientClinic.tasks.plural;
    }

    function handleSortingChange(updater: SortingState | ((old: SortingState) => SortingState)) {
        setSorting(updater);

        setPagination((prev) => ({
            pageIndex: 0,
            pageSize: prev.pageSize,
        }));
    }

    function renderContent() {
        if (isLoading) {
            return (
                <Box
                    display="flex"
                    justifyContent="center"
                >
                    <Loader />
                </Box>
            );
        }

        if (error) {
            return <Alert appearance="warning">{translate.outpatientClinic.tasks.errorFetchingTasks}</Alert>;
        }

        if (!tasks) {
            return (
                <EmptyState
                    icon="tasks"
                    subtitle={translate.contactsAndTasks.emptyPatientTaskListSubtitle}
                    title={translate.contactsAndTasks.emptyPatientTaskListTitle}
                    variant="centered"
                />
            );
        }

        return (
            <>
                <OutpatientClinicTaskListTable
                    onSortingChange={handleSortingChange}
                    sorting={sorting}
                    tableSelection={tableSelection}
                    tasks={tasks}
                    total={total}
                />
                {tasks.length > 0 && (
                    <Pagination
                        onChange={({ page: newPage, pageSize: newPageSize }) => {
                            setPagination({
                                pageIndex: newPage - 1,
                                pageSize: newPageSize,
                            });
                        }}
                        page={pagination.pageIndex + 1}
                        pageSize={pagination.pageSize}
                        totalItems={total}
                    />
                )}
                {tasks.length === 0 && <OutpatientClinicTaskListNoResults setFilter={setFilter} />}
            </>
        );
    }

    function renderActionBar() {
        return (
            <ActionBar
                isOpen={tableSelection.hasSelected()}
                onClose={() => tableSelection.clear()}
            >
                <Box
                    alignItems="center"
                    display="flex"
                    gap={3}
                    justifyContent="space-between"
                >
                    <BadgeWithLabel
                        color="inverse"
                        label={translate.common.isSelected}
                        value={tableSelection.getSize().toString()}
                    />

                    <SplitButton
                        items={[
                            {
                                label: translate.outpatientClinic.tasks.editPerformer,
                                onClick: () => openModal('editPerformer'),
                            },
                            {
                                label: translate.outpatientClinic.tasks.editExpectedDateCompleted,
                                onClick: () => openModal('editExpectedDateCompleted'),
                            },
                        ]}
                    />
                </Box>
            </ActionBar>
        );
    }

    function renderEditPerformerModal() {
        return (
            <Modal
                actionButtons={[
                    <Button
                        key="close"
                        onClick={closeModal}
                        variant="outline"
                    >
                        {translate.common.cancel}
                    </Button>,
                    <Button
                        isLoading={isSaving}
                        key="assign"
                        onClick={handlePerformerModalAssignClick}
                    >
                        {translate.outpatientClinic.tasks.assign}
                    </Button>,
                ]}
                isOpen={activeModal === 'editPerformer'}
                onClose={closeModal}
                size="medium"
                title={translate.outpatientClinic.tasks.assignTo}
            >
                <Text marginBottom={6}>
                    {formatString(translate.outpatientClinic.tasks.selectedAmountTasks, [
                        tableSelection.getSize().toString(),
                        getTaskTranslation(tableSelection.getSize()),
                    ])}
                </Text>

                <FormGroup
                    label={formatString(translate.outpatientClinic.tasks.assignSelectedTasksTo, [
                        getTaskTranslation(tableSelection.getSize()),
                    ])}
                    layout="vertical"
                >
                    <Select
                        error={getValidationErrorTranslation(
                            editPerformerFormData.errors.get('practitionerIdOrProfession')
                        )}
                        onChange={(value) => editPerformerFormData.update({ practitionerIdOrProfession: value?.value })}
                        options={getAvailableProfessionsAndPerformers()}
                        value={getAvailableProfessionsAndPerformers().find(
                            (option) => option.value == editPerformerFormData.formData.practitionerIdOrProfession
                        )}
                    />
                </FormGroup>
            </Modal>
        );
    }

    function renderEditExpectedDateCompletedModal() {
        return (
            <Modal
                actionButtons={[
                    <Button
                        key="close"
                        onClick={closeModal}
                        variant="outline"
                    >
                        {translate.common.cancel}
                    </Button>,
                    <Button
                        isLoading={isSaving}
                        key="assign"
                        onClick={handlexpectedDateCompletedAssignClick}
                    >
                        {translate.outpatientClinic.tasks.editExpectedDateCompleted}
                    </Button>,
                ]}
                isOpen={activeModal === 'editExpectedDateCompleted'}
                onClose={closeModal}
                size="medium"
                title={translate.outpatientClinic.tasks.editExpectedDateCompleted}
            >
                <Text marginBottom={6}>
                    {formatString(translate.outpatientClinic.tasks.selectedAmountTasks, [
                        tableSelection.getSize().toString(),
                        getTaskTranslation(tableSelection.getSize()),
                    ])}
                </Text>

                <FormGroup
                    label={translate.outpatientClinic.tasks.newExpectedDateCompleted}
                    layout="vertical"
                >
                    <DatePicker
                        error={getValidationErrorTranslation(
                            editExpectedDateCompletedFormData.errors.get('editExpectedDateCompleted')
                        )}
                        onChange={(value) =>
                            editExpectedDateCompletedFormData.update({ editExpectedDateCompleted: value })
                        }
                        value={editExpectedDateCompletedFormData.formData.editExpectedDateCompleted}
                    />
                </FormGroup>
            </Modal>
        );
    }

    function getAvailableProfessionsAndPerformers() {
        const availableProfessionsAndPerformers = new Map<
            Profession,
            {
                label: string;
                practitioners: Map<string, string>;
            }
        >();

        for (const performer of availablePerformers) {
            for (const employment of performer.employments) {
                const profession = employment.profession;

                if (profession === null) {
                    continue;
                }

                if (!availableProfessionsAndPerformers.has(profession)) {
                    availableProfessionsAndPerformers.set(profession, {
                        label: translate.professionEnum[profession],
                        practitioners: new Map(),
                    });
                }

                const professionGroup = availableProfessionsAndPerformers.get(profession);

                if (!professionGroup) {
                    continue;
                }

                professionGroup.practitioners.set(performer.id, getDisplayName(performer));
            }
        }

        const options = [];

        for (const [profession, data] of availableProfessionsAndPerformers) {
            options.push({
                isGroupHeader: true,
                label: data.label,
                value: profession,
            });

            Array.from(data.practitioners.entries()).forEach(([optionValue, optionLabel]) => {
                options.push({
                    isGroupOption: true,
                    label: optionLabel,
                    value: optionValue,
                });
            });
        }

        return options;
    }

    function handlePerformerModalAssignClick() {
        const validation = editPerformerFormData.validate();

        if (validation.hasErrors) {
            return;
        }

        const isProfession = Object.values(Profession).includes(
            editPerformerFormData.formData.practitionerIdOrProfession as Profession
        );

        commitUpdateTasks({
            [isProfession ? 'performerPractitionerProfession' : 'performerPractitionerId']:
                editPerformerFormData.formData.practitionerIdOrProfession,
        });
    }

    function handlexpectedDateCompletedAssignClick() {
        const validation = editExpectedDateCompletedFormData.validate();

        if (validation.hasErrors) {
            return;
        }

        commitUpdateTasks({
            expectedDateCompleted: editExpectedDateCompletedFormData.formData.editExpectedDateCompleted
                ? DateTime.from(editExpectedDateCompletedFormData.formData.editExpectedDateCompleted).format(
                      'YYYY-MM-DD'
                  )
                : null,
        });
    }

    function commitUpdateTasks(data: Record<string, unknown>) {
        setIsSaving(true);

        fetchJson({
            body: {
                ...data,
                outpatientClinicTaskIds: tableSelection.getSelected(),
            },
            method: 'PATCH',
            signal: abortSignal,
            url: '/api/outpatientClinic/tasks',
        }).then(() => {
            showToast(ToastType.success, translate.outpatientClinic.tasks.changesSuccessfullySaved);

            queryClient.refetchQueries({
                queryKey: ['outpatient-clinic-tasks-filtered'],
            });
            closeModal();

            setIsSaving(false);
        });
    }
}
